import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../context/DriverAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, LogOut } from 'lucide-react';

const DriverAgreementPage = () => {
  const navigate = useNavigate();
  const { driver, loading, isAuthenticated, hasSignedAgreement, signAgreement, logout } =
    useDriverAuth();
  const [agreementText, setAgreementText] = useState('');
  const [legalName, setLegalName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/driver/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!loading && hasSignedAgreement) {
      navigate('/driver/dashboard');
    }
  }, [loading, hasSignedAgreement, navigate]);

  useEffect(() => {
    if (driver?.name) {
      setLegalName(driver.name);
    }
  }, [driver?.name]);

  useEffect(() => {
    fetch('/driver-agreement.txt')
      .then((res) => res.text())
      .then(setAgreementText)
      .catch(() => setAgreementText('Unable to load agreement text. Please refresh and try again.'));
  }, []);

  const handleSign = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreed) {
      setError('You must confirm that you have read and agree to the agreement.');
      return;
    }
    if (!legalName.trim()) {
      setError('Enter your full legal name to sign.');
      return;
    }

    setSubmitting(true);
    const result = await signAgreement(legalName.trim());
    setSubmitting(false);

    if (result.success) {
      navigate('/driver/dashboard');
    } else {
      setError(result.error);
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-neutral-950 rounded-full">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Driver Agreement</h1>
              <p className="text-sm text-gray-600">Sign before accepting delivery assignments</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">
              Tribal Dash LLC — Independent Contractor Agreement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[420px] overflow-y-auto rounded-lg border bg-white p-4 text-sm leading-relaxed text-neutral-800 whitespace-pre-wrap">
              {agreementText || 'Loading agreement...'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Electronic signature</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSign} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="legal-name">Full legal name (signature)</Label>
                <Input
                  id="legal-name"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="Type your full legal name"
                  required
                />
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={(checked) => setAgreed(checked === true)}
                />
                <Label htmlFor="agree" className="text-sm leading-relaxed cursor-pointer">
                  I have read the entire Driver Independent Contractor Agreement, understand its
                  terms, had the opportunity to seek legal advice, and voluntarily agree to be bound
                  by it. I understand that my electronic signature has the same legal effect as a
                  handwritten signature.
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-neutral-950 hover:bg-black"
                size="lg"
                disabled={submitting || !agreementText}
              >
                {submitting ? 'Signing...' : 'Sign agreement & continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverAgreementPage;
